import React, { useState } from 'react';
import { MessageSquare, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

interface QueryEntry {
  id: string;
  content: string;
  timestamp: Date;
}

interface QueryHistorySidebarProps {
  queries: QueryEntry[];
  onQueryClick: (messageId: string) => void;
}

const QueryHistorySidebar: React.FC<QueryHistorySidebarProps> = ({
  queries,
  onQueryClick,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (queries.length === 0) return null;

  return (
    <div
      className={`flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col transition-all duration-200 ${
        collapsed ? 'w-12' : 'w-64'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-gray-800">
        {!collapsed && (
          <div className="flex items-center gap-2 text-sm font-medium text-gray-300">
            <Clock className="w-4 h-4" />
            <span>Query History</span>
          </div>
        )}
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className="p-1 text-gray-500 hover:text-gray-300 transition-colors rounded hover:bg-gray-800"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Query list */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto py-2">
          {queries.map((query, idx) => (
            <button
              key={query.id}
              onClick={() => onQueryClick(query.id)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-800 transition-colors group border-b border-gray-800/50"
              title={query.content}
            >
              <div className="flex items-start gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-gray-600 mt-0.5 flex-shrink-0 group-hover:text-blue-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-400 group-hover:text-gray-200 line-clamp-2 leading-relaxed">
                    {query.content}
                  </p>
                  <p className="text-[10px] text-gray-600 mt-1">
                    {formatTime(query.timestamp)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Collapsed icon list */}
      {collapsed && (
        <div className="flex-1 overflow-y-auto py-2">
          {queries.map((query, idx) => (
            <button
              key={query.id}
              onClick={() => onQueryClick(query.id)}
              className="w-full flex justify-center py-2 hover:bg-gray-800 transition-colors"
              title={query.content}
            >
              <MessageSquare className="w-3.5 h-3.5 text-gray-600 hover:text-blue-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function formatTime(date: Date): string {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  return `${h}:${minutes} ${ampm}`;
}

export default QueryHistorySidebar;
